import PreviousButton from "components/Previous/PreviousButton"
import { CHECKUP_END_PAGE, SURVEY_PAGE } from "core/constants"
import { useDispatch, useSelector } from "react-redux"
import surveyActions from "containers/Survey/services/actions"
import { useNavigate } from "react-router"
import { Button } from "components/Button"
import { vitals as vitalList } from "containers/Vitals/services/constants"
import { getSelectedPatientVitals } from "containers/Vitals/services/selector"
import { getCurrentSelectedPatientId } from "containers/Survey/services/selector"
import { useLanguage } from "locales"
import styles from "./CheckupReport.module.scss"
import { getCurrentTypeUnit } from "containers/Vitals/Vitals"
import { CaretLeftOutlined, CaretRightOutlined } from "@ant-design/icons"
import { Carousel as AntdCarousel, Col, Row } from "antd"
import { useEffect, useRef, useState } from "react"
import { CarouselRef } from "antd/lib/carousel"
import { Card } from "components/Card/Card"

const CHECKUP_REPORT_SLIDE_COUNT = 3

const DIAGNOSTIC_COLOR = {
  alert: "red",
  warning: "orange",
  ok: "green",
  info: "blue",
}

const CONSTANTS = {
  BMI: [15, 18.5, 25, 30, 40],
  SYSTOLIC: [100, 120, 130, 140, 160],
  DIASTOLIC: [60, 80, 85, 90, 100],
  HEART_RATE: [50, 60, 100, 120, 140],
  OXYGEN_SATURATION: [85, 90, 95, 100, 105],
}

const parseRawValue = (value?: string | number): number | null => {
  if (value === undefined || value === null) {
    return null
  }
  const normalized = String(value).replace(",", ".")
  const parsed = Number(normalized)
  if (Number.isNaN(parsed)) {
    return null
  }
  return parsed
}

const calculateBmi = (height: number, weight: number): number => {
  return Number((weight / (height * height)).toFixed(1))
}

const getBmiCategory = (bmi: number, languages: any): {
  label: string
  advice: string
  color: string
  index: number
} => {
  if (bmi < CONSTANTS.BMI[1]) {
    return {
      label: languages.checkupAdvices.bmi.labelUnder185,
      advice: languages.checkupAdvices.bmi.adviceUnder185,
      color: DIAGNOSTIC_COLOR.info,
      index: 1,
    }
  }
  if (bmi < CONSTANTS.BMI[2]) {
    return {
      label: languages.checkupAdvices.bmi.labelUnder25,
      advice: languages.checkupAdvices.bmi.adviceUnder25,
      color: DIAGNOSTIC_COLOR.ok,
      index: 2,
    }
  }
  if (bmi < CONSTANTS.BMI[3]) {
    return {
      label: languages.checkupAdvices.bmi.labelUnder30,
      advice: languages.checkupAdvices.bmi.adviceUnder30,
      color: DIAGNOSTIC_COLOR.warning,
      index: 3,
    }
  }
  return {
    label: languages.checkupAdvices.bmi.labelOver30,
    advice: languages.checkupAdvices.bmi.adviceOver30,
    color: DIAGNOSTIC_COLOR.alert,
    index: 4,
  }
}

const getMarkerPercent = (type: string, value: number, index: number): number => {

  const initialMarker = 25 * (index - 1);
  const proportionMarker = Math.max(0, Math.min((value - CONSTANTS[type][index - 1]) / (CONSTANTS[type][index] - CONSTANTS[type][index - 1]) * 25, 25));
  return initialMarker + proportionMarker;
}

const BmiCard = ({ vitals, languages }: { vitals: any, languages: any }) => {
  const height = vitals?.[vitalList.HEIGHT]?.value?.raw;
  const weight = vitals?.[vitalList.WEIGHT]?.value?.raw;
  const heightRaw = parseRawValue(height);
  const weightRaw = parseRawValue(weight);
  const heightInMeters = heightRaw ? heightRaw / 100 : null
  const bmi = heightInMeters && weightRaw ? calculateBmi(heightInMeters, weightRaw) : 0;
  const category = getBmiCategory(bmi, languages);
  const markerPercent = getMarkerPercent("BMI", bmi, category.index)
  const unit = getCurrentTypeUnit(vitalList.BMI);
  return (
    <div className={styles.VitalCard}>
      <h2 className={styles.VitalTitle}>{languages.checkupAdvices.bmi.massLabel}</h2>

      {
        height && weight ? (
          <>
            <div className={styles.VitalValue}>
              <span className={styles.VitalNumber}>{bmi.toFixed(1)}</span>
              <span className={styles.VitalUnit}>{unit}</span>
            </div>
            <div className={`${styles.VitalBadge} ${styles[category.color]}`}>{category.label}</div>
            <div className={styles.Gauge}>
              <div className={styles.GaugeTrack}>
                <div
                  className={`${styles.GaugeProgress} ${styles[category.color]}`}
                  style={{ width: `${markerPercent}%` }}
                />
                <span
                  className={`${styles.GaugeMarker} ${styles[category.color]}`}
                  style={{ left: `${markerPercent}%` }}
                />

                <span className={styles.GaugeSeparator} style={{ left: "25%" }} />
                <span className={styles.GaugeSeparator} style={{ left: "50%" }} />
                <span className={styles.GaugeSeparator} style={{ left: "75%" }} />
              </div>
              <div className={styles.GaugeLabels} style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
                <div>
                  <span className={styles.Range}>&lt;{CONSTANTS.BMI[1]}</span>
                  <span className={styles.RangeLabel}>Maigre</span>
                </div>
                <div>
                  <span className={styles.Range}>{CONSTANTS.BMI[1]}-{CONSTANTS.BMI[2]}</span>
                  <span className={styles.RangeLabel}>Normal</span>
                </div>
                <div>
                  <span className={styles.Range}>{CONSTANTS.BMI[2]}-{CONSTANTS.BMI[3]}</span>
                  <span className={styles.RangeLabel}>Surpoids</span>
                </div>
                <div>
                  <span className={styles.Range}>&gt;{CONSTANTS.BMI[3]}</span>
                  <span className={styles.RangeLabel}>Obésité</span>
                </div>
              </div>
            </div>
            <p className={styles.Advice}>{category.advice}</p>
          </>
        ) : (
          <>
            <div className={styles.MissingData}>{languages.checkupMissingData}</div>
            <div></div>
          </>
        )
      }
    </div>
  )
}

const getOxygenSaturationCategory = (oxygenSaturation: number, languages: any): {
  label: string
  advice: string
  color: string
  index: number
} => {
  if (oxygenSaturation < CONSTANTS.OXYGEN_SATURATION[1]) {
    return {
      label: languages.checkupAdvices.oxygenSaturation.labelUnder90,
      advice: languages.checkupAdvices.oxygenSaturation.adviceUnder90,
      color: DIAGNOSTIC_COLOR.alert,
      index: 1,
    }
  }
  if (oxygenSaturation < CONSTANTS.OXYGEN_SATURATION[2]) {
    return {
      label: languages.checkupAdvices.oxygenSaturation.labelUnder95,
      advice: languages.checkupAdvices.oxygenSaturation.adviceUnder95,
      color: DIAGNOSTIC_COLOR.warning,
      index: 2,
    }
  }
  return {
    label: languages.checkupAdvices.oxygenSaturation.labelOver95,
    advice: languages.checkupAdvices.oxygenSaturation.adviceOver95,
    color: DIAGNOSTIC_COLOR.ok,
    index: 3,
  }
}

const OxygenSaturationCard = ({ vitals, languages }: { vitals: any, languages: any }) => {
  const oxygenSaturation = vitals?.[vitalList.SATURATION]?.value?.raw;
  const oxygenSaturationRaw = parseRawValue(oxygenSaturation);
  const unit = getCurrentTypeUnit(vitalList.SATURATION);
  const category = getOxygenSaturationCategory(oxygenSaturationRaw, languages);
  const markerPercent = Math.max(0, Math.min((oxygenSaturationRaw - CONSTANTS.OXYGEN_SATURATION[0]) / (CONSTANTS.OXYGEN_SATURATION[3] - CONSTANTS.OXYGEN_SATURATION[0]) * 100, 100))
  return (
    <div className={styles.VitalCard}>
      <h2 className={styles.VitalTitle}>{languages.checkupAdvices.oxygenSaturation.oxygenSaturationLabel}</h2>

      {
        oxygenSaturationRaw ? (
          <>
            <div className={styles.VitalValue}>
              <span className={styles.VitalNumber}>{oxygenSaturationRaw}</span>
              <span className={styles.VitalUnit}>{unit}</span>
            </div>
            <div className={`${styles.VitalBadge} ${styles[category.color]}`}>{category.label}</div>
            <div className={styles.Gauge}>
              <div className={styles.GaugeTrack}>
                <div
                  className={`${styles.GaugeProgress} ${styles[category.color]}`}
                  style={{ width: `${markerPercent}%` }}
                />
                <span
                  className={`${styles.GaugeMarker} ${styles[category.color]}`}
                  style={{ left: `${markerPercent}%` }}
                />
                <span className={styles.GaugeSeparator} style={{ left: "33%" }} />
                <span className={styles.GaugeSeparator} style={{ left: "66%" }} />
              </  div>
              <div className={styles.GaugeLabels} style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                <div>
                  <span className={styles.Range}>&lt;{CONSTANTS.OXYGEN_SATURATION[1]}</span>
                  <span className={styles.RangeLabel}>{languages.checkupAdvices.oxygenSaturation.labelUnder90}</span>
                </div>
                <div>
                  <span className={styles.Range}>{CONSTANTS.OXYGEN_SATURATION[1]}-{CONSTANTS.OXYGEN_SATURATION[2]}</span>
                  <span className={styles.RangeLabel}>{languages.checkupAdvices.oxygenSaturation.labelUnder95}</span>
                </div>
                <div>
                  <span className={styles.Range}>{CONSTANTS.OXYGEN_SATURATION[2]}-{CONSTANTS.OXYGEN_SATURATION[3]}</span>
                  <span className={styles.RangeLabel}>{languages.checkupAdvices.oxygenSaturation.labelOver95}</span>
                </div>
              </div>
            </div>
            <p className={styles.Advice}>{category.advice}</p>
          </>
        ) : (
          <>
            <div className={styles.MissingData}>{languages.checkupMissingData}</div>
            <div></div>
          </>
        )
      }
    </div>
  )
}

const getSystolicCategory = (systolic: number): { color: string, index: number } => {
  if (systolic < CONSTANTS.SYSTOLIC[1]) {
    return { color: DIAGNOSTIC_COLOR.info, index: 1 };
  }
  if (systolic < CONSTANTS.SYSTOLIC[2]) {
    return { color: DIAGNOSTIC_COLOR.ok, index: 2 };
  }
  if (systolic < CONSTANTS.SYSTOLIC[3]) {
    return { color: DIAGNOSTIC_COLOR.warning, index: 3 };
  }
  return { color: DIAGNOSTIC_COLOR.alert, index: 4 };
}

const getDiastolicCategory = (diastolic: number): { color: string, index: number } => {
  if (diastolic < CONSTANTS.DIASTOLIC[1]) {
    return { color: DIAGNOSTIC_COLOR.info, index: 1 };
  }
  if (diastolic < CONSTANTS.DIASTOLIC[2]) {
    return { color: DIAGNOSTIC_COLOR.ok, index: 2 };
  }
  if (diastolic < CONSTANTS.DIASTOLIC[3]) {
    return { color: DIAGNOSTIC_COLOR.warning, index: 3 };
  }
  return { color: DIAGNOSTIC_COLOR.alert, index: 4 };
}

const getHeartRateCategory = (heartRate: number): { color: string, index: number } => {
  if (heartRate < CONSTANTS.HEART_RATE[1]) {
    return { color: DIAGNOSTIC_COLOR.info, index: 1 };
  }
  if (heartRate < CONSTANTS.HEART_RATE[2]) {
    return { color: DIAGNOSTIC_COLOR.ok, index: 2 };
  }
  if (heartRate < CONSTANTS.HEART_RATE[3]) {
    return { color: DIAGNOSTIC_COLOR.warning, index: 3 };
  }
  return { color: DIAGNOSTIC_COLOR.alert, index: 4 };
}

const getTensionGlobalCategory = (systolic: number | null, diastolic: number | null, languages: any): { advice: string, label: string, color: string } => {
  const getSystolicLevel = (s: number): number => {
    if (s < 120) return 1;
    if (s < 130) return 0;
    if (s < 140) return 3;
    return 4;
  }
  const getDiastolicLevel = (d: number): number => {
    if (d < 80) return 1;
    if (d < 85) return 0;
    if (d < 90) return 3;
    return 4;
  }

  const level = Math.max(
    systolic ? getSystolicLevel(systolic) : 0,
    diastolic ? getDiastolicLevel(diastolic) : 0,
  );

  switch (level) {
    case 4:
      return { label: languages.checkupAdvices.heartRate.labelHypertension, advice: languages.checkupAdvices.heartRate.adviceHypertension, color: DIAGNOSTIC_COLOR.alert };
    case 3:
      return { label: languages.checkupAdvices.heartRate.labelHigh, advice: languages.checkupAdvices.heartRate.adviceHigh, color: DIAGNOSTIC_COLOR.warning };
    case 0:
      return { label: languages.checkupAdvices.heartRate.labelNormal, advice: languages.checkupAdvices.heartRate.generalAdvice, color: DIAGNOSTIC_COLOR.ok };
    default:
      return { label: languages.checkupAdvices.heartRate.labelLow, advice: languages.checkupAdvices.heartRate.adviceLow, color: DIAGNOSTIC_COLOR.info };
  }
}

const HeartRateCard = ({ vitals, languages }: { vitals: any, languages: any }) => {
  const heartRate = vitals?.[vitalList.HEARTRATE]?.value?.raw;
  const heartRateRaw = parseRawValue(heartRate);
  const systolic = vitals?.[vitalList.TENSION]?.value?.systolic?.raw;
  const systolicRaw = parseRawValue(systolic);
  const diastolic = vitals?.[vitalList.TENSION]?.value?.diastolic?.raw;
  const diastolicRaw = parseRawValue(diastolic);

  const systolicCategory = systolicRaw ? getSystolicCategory(systolicRaw) : null;
  const diastolicCategory = diastolicRaw ? getDiastolicCategory(diastolicRaw) : null;
  const heartRateCategory = heartRateRaw ? getHeartRateCategory(heartRateRaw) : null;

  const systolicMarker = systolicRaw ? getMarkerPercent("SYSTOLIC", systolicRaw, systolicCategory.index) : 0;
  const diastolicMarker = diastolicRaw ? getMarkerPercent("DIASTOLIC", diastolicRaw, diastolicCategory.index) : 0;
  const heartRateMarker = heartRateRaw ? getMarkerPercent("HEART_RATE", heartRateRaw, heartRateCategory.index) : 0;

  const { advice: globalAdvice, label: globalLabel, color: globalColor } = getTensionGlobalCategory(systolicRaw, diastolicRaw, languages);

  const gaugesData = [
    {
      label: "Systolique",
      value: systolicRaw,
      unit: getCurrentTypeUnit(vitalList.TENSION),
      color: systolicCategory?.color,
      marker: systolicMarker,
    },
    {
      label: "Diastolique",
      value: diastolicRaw,
      unit: getCurrentTypeUnit(vitalList.TENSION),
      color: diastolicCategory?.color,
      marker: diastolicMarker,
    },
    {
      label: "Rythme cardiaque",
      value: heartRateRaw,
      unit: getCurrentTypeUnit(vitalList.HEARTRATE),
      color: heartRateCategory?.color,
      marker: heartRateMarker,
    },
  ]
  return (
    <div className={styles.VitalCard}>
      <h2 className={styles.VitalTitle}>{languages.checkupAdvices.heartRate.heartRateLabel}</h2>
      {systolicRaw && diastolicRaw && heartRateRaw ? (
        <>
          <div className={`${styles.VitalBadge} ${styles[globalColor]}`}>{globalLabel}</div>
          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "30px" }}>
            {gaugesData.map((gauge, index) => (
              <div key={`gauge-${index}`} className={styles.Gauge}>
                <div className={styles.GaugeHeader}>
                  <div className={styles.GaugeHeaderItem}>{gauge.label}</div>
                  <div className={styles.GaugeHeaderValue}>{gauge.value} {gauge.unit}</div>
                </div>
                <div className={styles.GaugeTrack}>
                  <div
                    className={`${styles.GaugeProgress} ${styles[gauge.color]}`}
                    style={{ width: `${gauge.marker}%` }}
                  />
                  <span
                    className={`${styles.GaugeMarker} ${styles[gauge.color]}`}
                    style={{ left: `${gauge.marker}%` }}
                  />

                  <span className={styles.GaugeSeparator} style={{ left: `25%` }} />
                  <span className={styles.GaugeSeparator} style={{ left: `50%` }} />
                  <span className={styles.GaugeSeparator} style={{ left: `75%` }} />
                </div>
              </div>
            ))}
          </div>
          <p className={styles.Advice}>{globalAdvice}</p>
        </>
      )
        : (
          <>
            <div className={styles.MissingData}>{languages.checkupMissingData}</div>
            <div></div>
          </>
        )
      }
    </div>
  )
}

const CheckupReport: React.FC = (): JSX.Element => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { languages } = useLanguage()
  const vitals = useSelector(getSelectedPatientVitals) as any;
  const selectedPatientId = useSelector(getCurrentSelectedPatientId) as number | undefined;
  const carouselRef = useRef<CarouselRef>(null)
  const [hideArrows, setHideArrows] = useState({ left: true, right: false })

  const onCarouselBeforeChange = (_from: number, to: number): void => {
    setHideArrows({
      left: to <= 0,
      right: to >= CHECKUP_REPORT_SLIDE_COUNT - 1,
    })
  }

  // A patient who skipped every measure has no vitals at all: the cards handle it
  // with a "missing data" message. Only a missing patient means we lost the survey context.
  useEffect(() => {
    if (selectedPatientId === undefined) {
      dispatch(surveyActions.reset())
      navigate(SURVEY_PAGE)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatientId])

  const previous = () => {
    carouselRef.current?.prev()
  }
  const next = () => {
    if (hideArrows.right) {
      dispatch(surveyActions.next())
      navigate(CHECKUP_END_PAGE)
    } else {
      carouselRef.current?.next()
    }
  }

  return (
    <>
      <PreviousButton
        onClick={() => {
          dispatch(surveyActions.previous())
          navigate(SURVEY_PAGE)
        }}
      />
      <Card title={languages.yourCheckupHealth} style={{ marginBottom: "0" }} doNotAnimateTitle={true}>
      <div className={styles.CheckupReportContainer}>
        <div className={styles.CheckupReport}>

          <Row className={styles.CheckupCarouselRow} align="middle" wrap={false}>
            <Col span={2} style={{ textAlign: "right" }}>
              <CaretLeftOutlined
                className={styles.CheckupCarouselArrow}
                onClick={previous}
                style={{
                  visibility: hideArrows.left ? "hidden" : "visible",
                  pointerEvents: hideArrows.left ? "none" : "auto",
                }}
              />
            </Col>
            <Col span={20} style={{ textAlign: "center" }}>
              <AntdCarousel
                ref={carouselRef}
                beforeChange={onCarouselBeforeChange}
                dots={false}
                infinite={false}
                style={{ height: "100%" }}
              >
                <div className={styles.CheckupCarouselItem}>
                  <BmiCard vitals={vitals} languages={languages} />
                </div>
                <div className={styles.CheckupCarouselItem}>
                  <HeartRateCard vitals={vitals} languages={languages} />
                </div>
                <div className={styles.CheckupCarouselItem}>
                  <OxygenSaturationCard vitals={vitals} languages={languages} />
                </div>
              </AntdCarousel>
            </Col>
            <Col span={2} style={{ textAlign: "left" }}>
              <CaretRightOutlined
                className={styles.CheckupCarouselArrow}
                onClick={next}
                style={{
                  visibility: hideArrows.right ? "hidden" : "visible",
                  pointerEvents: hideArrows.right ? "none" : "auto",
                }}
              />
            </Col>
          </Row>

        </div>
        <div style={{ display: "flex", gap: "100px", width: "1000px" }}>
          <Button
            type="default"
            wide="long"
            htmlType="button"
            onClick={previous}
            disabled={hideArrows.left}
            style={{ width: "100%", flex: 1 }}
          >
            {languages.previousButton}
          </Button> 
          <Button
            type="primary"
            wide="long"
            htmlType="button"
            onClick={next}
            style={{ width: "100%", flex: 1 }}
          >
            { hideArrows.right ? languages.continue : languages.next}
          </Button>
        </div>
      </div>
      </Card>
    </>
  )
}

export default CheckupReport
